import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { AlertCircle, Check, ExternalLink, Pencil, Plus, Trash2 } from 'lucide-react';
import type {
  BusinessProfileDraft,
  BusinessProfileOfferDraft,
  EvidenceBackedProfileField,
} from '@mouja/domain/analysis';

import { useLanguage } from '../../i18n/language.js';
import { newIdempotencyKey } from '../../workspace/api.js';
import { useSession } from '../../workspace/session.js';
import { InlineAlert, Spinner } from '../../workspace/ui.js';
import { GeographyCombobox } from '../../merchant/GeographyCombobox.js';
import { MerchantAuthModal } from './MerchantAuthModal.js';
import {
  approveMerchantProfile,
  claimMerchantIntake,
  correctMerchantProfile,
  fetchCurrentMerchantProfile,
  fetchMerchantIntake,
  MERCHANT_INTAKE_STORAGE_KEY,
  MERCHANT_PROFILE_INTENT_STORAGE_KEY,
  processMerchantIntake,
  type MerchantProfileView,
} from '../../merchant/api.js';
import { missingProfileConfirmationFields } from '../../merchant/profileCompleteness.js';
import { useMerchantWorkspaceIdentity } from '../../merchant/shell-context.js';

const MAX_ANALYSIS_POLL_MS = 30_000;

const PROFILE_COPY = {
  en: {
    building: 'Building your business profile…',
    checkingTitle: 'Mouja is checking your information',
    checkingBody:
      'Mouja is checking the information you provided. No action is required from you; completed work remains saved.',
    evidenceTitle: 'We need a little more evidence',
    evidenceBody: 'The supplied sources did not produce a usable profile.',
    breadcrumb: 'Breadcrumb',
    productionLines: 'Production Lines',
    title: 'Business Profile',
    confirmed: 'Confirmed',
    intro: 'Review what Mouja understood from your sources.',
    correctTitle: 'Correct Business Profile',
    correctHelp:
      'Update anything Mouja misunderstood. Your sources remain attached to this profile.',
    businessName: 'Business name',
    businessDoes: 'What the business does',
    geography: 'Geography',
    offers: 'Offers',
    offerRequired: 'At least one required',
    offer: 'Offer',
    removeOffer: 'Remove Offer',
    addOffer: 'Add Offer',
    cancel: 'Cancel',
    save: 'Save',
    saving: 'Saving…',
    noOffers: 'No Offers yet — at least one is required',
    noOffersHelp:
      'An Offer is one thing the business actually sells. Add it in Edit details.',
    sources: 'Sources',
    sourceReadOnly: 'Confirmed · Read-only',
    sourceNote: 'Sources stay attached to this profile and are read-only here.',
    opensNewTab: 'Opens in a new tab',
    actions: 'Business Profile actions',
    completeTitle: 'Complete your Business Profile',
    stillNeeded: 'Still needed',
    readyTitle: 'Ready to confirm?',
    readyBody: 'Confirm this profile so Mouja can continue your analysis.',
    confirming: 'Confirming…',
    confirm: 'Confirm business profile',
    edit: 'Edit details',
    confirmedTitle: 'Business Profile confirmed',
    confirmedBody:
      'Mouja can continue in the background. You do not need to keep this page open.',
    editProfile: 'Edit Business Profile',
    restoreTitle: 'We couldn’t restore your Business Profile',
    retry: 'Retry',
    missingTitle: 'No business profile found',
    missingBody: 'Start from your controlled Mouja pilot link to generate a profile.',
  },
  ar: {
    building: 'نبني ملف نشاطك التجاري…',
    checkingTitle: 'موجة تتأكد من معلوماتك',
    checkingBody:
      'موجة تتأكد من المعلومات اللي عطيتها. ما يلزمك تسوي شي، والشغل اللي خلص محفوظ.',
    evidenceTitle: 'نحتاج أدلة أكثر شوي',
    evidenceBody: 'المصادر اللي عطيتنا ما طلع منها ملف نقدر نستخدمه.',
    breadcrumb: 'مسار التنقل',
    productionLines: 'خطوط الإنتاج',
    title: 'الملف التجاري',
    confirmed: 'مؤكد',
    intro: 'راجع اللي فهمته موجة من مصادرك.',
    correctTitle: 'تصحيح الملف التجاري',
    correctHelp: 'عدّل أي معلومة ما فهمتها موجة صح. ومصادرك تبقى مرتبطة بهذا الملف.',
    businessName: 'اسم النشاط التجاري',
    businessDoes: 'وش يقدم نشاطك التجاري',
    geography: 'الموقع الجغرافي',
    offers: 'العروض',
    offerRequired: 'لازم عرض واحد على الأقل',
    offer: 'العرض',
    removeOffer: 'شِيل العرض',
    addOffer: 'ضيف عرض',
    cancel: 'إلغاء',
    save: 'حفظ',
    saving: 'جاري الحفظ…',
    noOffers: 'ما فيه عروض لين الحين — لازم عرض واحد على الأقل',
    noOffersHelp: 'العرض هو شي يبيعه نشاطك فعلًا. ضيفه من تعديل التفاصيل.',
    sources: 'المصادر',
    sourceReadOnly: 'مؤكد · للقراءة بس',
    sourceNote: 'المصادر تبقى مرتبطة بهذا الملف، وهي للقراءة بس هنا.',
    opensNewTab: 'يفتح في علامة تبويب جديدة',
    actions: 'إجراءات الملف التجاري',
    completeTitle: 'كمّل ملف نشاطك التجاري',
    stillNeeded: 'لسه مطلوب',
    readyTitle: 'جاهز للتأكيد؟',
    readyBody: 'أكّد هذا الملف عشان موجة تكمل التحليل.',
    confirming: 'جاري التأكيد…',
    confirm: 'أكّد الملف التجاري',
    edit: 'عدّل التفاصيل',
    confirmedTitle: 'تأكّد الملف التجاري',
    confirmedBody: 'موجة تقدر تكمل في الخلفية. ما يلزمك تخلي الصفحة هذي مفتوحة.',
    editProfile: 'تعديل الملف التجاري',
    restoreTitle: 'ما قدرنا نستعيد ملف نشاطك التجاري',
    retry: 'جرّب مرة ثانية',
    missingTitle: 'ما لقينا ملف تجاري',
    missingBody: 'ابدأ من رابط تجربة موجة المخصص لك عشان نسوي لك ملف.',
  },
} as const;

function localizedMissingField(field: string, language: 'en' | 'ar'): string {
  if (language === 'en') return field;
  if (field === 'your business name') return 'اسم نشاطك التجاري';
  if (field === 'what your business does') return 'وش يقدمه نشاطك التجاري';
  if (field === 'where your business operates') return 'وين يشتغل نشاطك';
  if (field === 'at least one concrete offer') return 'عرض فعلي واحد على الأقل';
  return field;
}

function rememberedIntake(): string | null {
  try {
    return localStorage.getItem(MERCHANT_INTAKE_STORAGE_KEY);
  } catch {
    return null;
  }
}

function rememberedProfileIntent(): 'confirm' | 'correct' | null {
  try {
    const value = sessionStorage.getItem(MERCHANT_PROFILE_INTENT_STORAGE_KEY);
    return value === 'confirm' || value === 'correct' ? value : null;
  } catch {
    return null;
  }
}

function clearProfileIntent(): void {
  try {
    sessionStorage.removeItem(MERCHANT_PROFILE_INTENT_STORAGE_KEY);
  } catch {
    // The profile remains claimable; only the convenience continuation is lost.
  }
}

function confirmed(field: EvidenceBackedProfileField, value: string) {
  return {
    ...field,
    value: value.trim() || null,
    basis: 'OWNER_CONFIRMED' as const,
    reviewState: 'OWNER_CONFIRMED' as const,
  };
}

function value(field: EvidenceBackedProfileField): string {
  return field.value ?? 'Not established yet';
}

function merchantField(value: string | null): EvidenceBackedProfileField {
  return {
    value: value?.trim() || null,
    basis: value?.trim() ? 'OWNER_CONFIRMED' : 'UNKNOWN',
    confidence: null,
    evidenceIds: [],
    reviewState: value?.trim() ? 'OWNER_CONFIRMED' : 'UNKNOWN',
  };
}

function newOffer(title: string, index: number): BusinessProfileOfferDraft {
  const unknown = () => merchantField(null);
  return {
    localId: `merchant-offer-${Date.now()}-${String(index)}`,
    title: merchantField(title),
    category: unknown(),
    description: unknown(),
    audienceOrNeed: unknown(),
    deliveryMode: unknown(),
    geographyOrAccess: unknown(),
    price: unknown(),
    proof: [],
    conversionPath: unknown(),
  };
}

function merchantSourceIdentity(
  source: MerchantProfileView['business']['sources'][number],
): string {
  const identity = source.externalIdentity ?? new URL(source.canonicalUrl).hostname;
  return source.platform === 'INSTAGRAM' || source.platform === 'TIKTOK'
    ? identity.startsWith('@')
      ? identity
      : `@${identity}`
    : identity;
}

export function MerchantProfilePage({
  intakeIdOverride,
  origin = 'business-profile',
}: {
  readonly intakeIdOverride?: string;
  readonly origin?: 'production-lines' | 'business-profile';
} = {}): ReactNode {
  const { language } = useLanguage();
  const copy = PROFILE_COPY[language];
  const { session } = useSession();
  const { setBusinessName, setProfileConfirmed } = useMerchantWorkspaceIdentity();
  const [searchParams] = useSearchParams();
  const requestedId = useMemo(
    () => intakeIdOverride ?? searchParams.get('intake') ?? rememberedIntake(),
    [intakeIdOverride, searchParams],
  );
  const [view, setView] = useState<MerchantProfileView | null>(null);
  const id = requestedId ?? view?.id ?? null;
  const [error, setError] = useState<string | null>(null);
  const [working, setWorking] = useState(false);
  const [authIntent, setAuthIntent] = useState<'confirm' | 'correct' | null>(null);
  const [editing, setEditing] = useState(false);
  const [identity, setIdentity] = useState('');
  const [niche, setNiche] = useState('');
  const [geography, setGeography] = useState('');
  const [offerTitles, setOfferTitles] = useState<string[]>([]);
  const loadInFlight = useRef<Promise<boolean> | null>(null);
  const loadController = useRef<AbortController | null>(null);
  const loadWasAborted = useRef(false);
  const mounted = useRef(true);
  const failedPolls = useRef(0);
  const showProfile = searchParams.get('view') === 'profile';
  const merchant =
    session.status === 'authenticated' && session.account.role === 'MERCHANT';

  useEffect(() => {
    if (view?.business.displayName !== undefined) {
      setBusinessName(view.business.displayName);
      setProfileConfirmed(view.profile?.reviewState === 'APPROVED');
    }
  }, [
    setBusinessName,
    setProfileConfirmed,
    view?.business.displayName,
    view?.profile?.reviewState,
  ]);

  const load = useCallback(async () => {
    if (id === null && !merchant) return false;
    if (loadInFlight.current !== null) return loadInFlight.current;
    const controller = new AbortController();
    loadController.current = controller;
    const request = (async () => {
      try {
        let next =
          id === null
            ? await fetchCurrentMerchantProfile(controller.signal)
            : await fetchMerchantIntake(id, controller.signal);
        if (
          next.profile?.reviewState !== 'APPROVED' &&
          (next.status === 'COLLECTING' || next.status === 'FAILED')
        ) {
          next = await processMerchantIntake(next.id, controller.signal);
        }
        if (!controller.signal.aborted && mounted.current) {
          setView(next);
          setError(null);
        }
        loadWasAborted.current = controller.signal.aborted;
        return !controller.signal.aborted;
      } catch (reason) {
        loadWasAborted.current = controller.signal.aborted;
        if (!controller.signal.aborted && mounted.current) {
          setError(reason instanceof Error ? reason.message : 'Profile unavailable.');
        }
        return false;
      }
    })();
    loadInFlight.current = request;
    void request.finally(() => {
      if (loadInFlight.current === request) loadInFlight.current = null;
      if (loadController.current === controller) loadController.current = null;
    });
    return request;
  }, [id, merchant]);

  const refreshProgress = useCallback(async () => load(), [load]);

  useEffect(() => {
    let effectActive = true;
    mounted.current = true;
    void load().then((success) => {
      // React's development StrictMode intentionally mounts, aborts and mounts
      // again. The second mount may briefly share the first aborted promise;
      // retry it once instead of leaving the customer on an endless spinner.
      if (!success && loadWasAborted.current && effectActive && mounted.current) {
        void load();
      }
    });
    return () => {
      effectActive = false;
      mounted.current = false;
      loadController.current?.abort();
    };
  }, [load]);

  // Processing can outlive the request that started it (or another tab may
  // hold the lease), so keep the functional preview moving without asking the
  // merchant to refresh. This polling can be replaced with the eventual
  // designed progress experience without changing the intake contract.
  useEffect(() => {
    if (view?.status !== 'PROCESSING') return undefined;
    const timer = window.setTimeout(() => void load(), 1_500);
    return () => {
      window.clearTimeout(timer);
    };
  }, [load, view?.status]);

  useEffect(() => {
    if (
      id === null ||
      view === null ||
      view.claimed ||
      session.status !== 'authenticated' ||
      session.account.role !== 'MERCHANT'
    ) {
      return;
    }
    setWorking(true);
    void claimMerchantIntake(id)
      .then(async (claimed) => {
        setView(claimed);
        setAuthIntent(null);
        const intent = rememberedProfileIntent();
        if (intent === null || claimed.profile === null) return;
        clearProfileIntent();
        if (intent === 'correct') {
          setIdentity(claimed.profile.payload.identity.value ?? '');
          setNiche(claimed.profile.payload.niche.value ?? '');
          setGeography(claimed.profile.payload.geography.value ?? '');
          setOfferTitles(
            claimed.profile.payload.offers.map((offer) => offer.title.value ?? ''),
          );
          setEditing(true);
          return;
        }
        const missing = missingProfileConfirmationFields(claimed.profile.payload);
        if (missing.length > 0) {
          setError(
            `Correct the profile before confirming it. Missing: ${missing.join(', ')}.`,
          );
          return;
        }
        await approveMerchantProfile(
          id,
          claimed.profile.artifactId,
          newIdempotencyKey(),
        );
        await load();
      })
      .catch((reason: unknown) => {
        setError(reason instanceof Error ? reason.message : 'Claim failed.');
      })
      .finally(() => {
        setWorking(false);
      });
  }, [id, load, view, session]);

  // The durable server runner owns continuation. This page only follows its
  // progress, so closing the tab cannot interrupt analysis.
  useEffect(() => {
    if (
      view?.profile?.reviewState !== 'APPROVED' ||
      !view.analysis.progress.polling.recommended ||
      view.analysis.progress.polling.afterMs === null
    ) {
      return undefined;
    }
    const pollAfterMs = view.analysis.progress.polling.afterMs;
    let cancelled = false;
    let timer: number | undefined;
    const poll = async () => {
      if (!navigator.onLine) {
        failedPolls.current = Math.min(failedPolls.current + 1, 4);
      } else {
        const success = await load();
        failedPolls.current = success ? 0 : Math.min(failedPolls.current + 1, 4);
      }
      if (!cancelled) {
        const delay = Math.min(
          pollAfterMs * 2 ** failedPolls.current,
          MAX_ANALYSIS_POLL_MS,
        );
        timer = window.setTimeout(() => void poll(), delay);
      }
    };
    timer = window.setTimeout(() => void poll(), pollAfterMs);
    return () => {
      cancelled = true;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [
    load,
    view?.profile?.reviewState,
    view?.analysis.progress.polling.afterMs,
    view?.analysis.progress.polling.recommended,
  ]);

  const openAuth = useCallback((intent: 'confirm' | 'correct') => {
    setAuthIntent(intent);
  }, []);

  if (id === null && !merchant) {
    return <MissingProfile language={language} />;
  }
  if (error !== null && view === null) {
    return (
      <ProfileLoadError
        detail={error}
        language={language}
        retry={() => void refreshProgress()}
      />
    );
  }
  if (view === null || ['COLLECTING', 'PROCESSING'].includes(view.status)) {
    return (
      <Centered>
        <Spinner label={copy.building} />
      </Centered>
    );
  }
  if (
    view.profileIssue.state === 'mouja_checking' &&
    view.profile?.reviewState !== 'APPROVED'
  ) {
    return (
      <Centered>
        <InlineAlert tone="warning" title={copy.checkingTitle}>
          {copy.checkingBody}
        </InlineAlert>
      </Centered>
    );
  }
  if (view.profile === null) {
    return (
      <Centered>
        <InlineAlert tone="warning" title={copy.evidenceTitle}>
          {copy.evidenceBody}
        </InlineAlert>
      </Centered>
    );
  }

  const profile = view.profile.payload;
  const profileArtifactId = view.profile.artifactId;
  const missingConfirmationFields = missingProfileConfirmationFields(profile);
  const beginEdit = () => {
    setIdentity(profile.identity.value ?? '');
    setNiche(profile.niche.value ?? '');
    setGeography(profile.geography.value ?? '');
    setOfferTitles(profile.offers.map((offer) => offer.title.value ?? ''));
    setEditing(true);
  };

  const save = async () => {
    if (!merchant) {
      openAuth('correct');
      return;
    }
    setWorking(true);
    try {
      const corrected: BusinessProfileDraft = {
        ...profile,
        identity: confirmed(profile.identity, identity),
        niche: confirmed(profile.niche, niche),
        geography: confirmed(profile.geography, geography),
        offers: offerTitles
          .map((title, index) => {
            const existing = profile.offers[index];
            return existing === undefined
              ? newOffer(title, index)
              : { ...existing, title: confirmed(existing.title, title) };
          })
          .filter((offer) => offer.title.value !== null),
      };
      await correctMerchantProfile(
        view.id,
        {
          expectedArtifactId: profileArtifactId,
          profile: corrected,
          reason: 'Merchant corrected the generated business profile.',
        },
        newIdempotencyKey(),
      );
      setEditing(false);
      await load();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Correction failed.');
    } finally {
      setWorking(false);
    }
  };

  const approve = async () => {
    if (!merchant) {
      openAuth('confirm');
      return;
    }
    if (missingConfirmationFields.length > 0) {
      setError(
        `Correct the profile before confirming it. Missing: ${missingConfirmationFields.join(', ')}.`,
      );
      return;
    }
    setWorking(true);
    try {
      await approveMerchantProfile(view.id, profileArtifactId, newIdempotencyKey());
      await load();
      setError(null);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Approval failed.');
    } finally {
      setWorking(false);
    }
  };

  // An approved profile belongs to the signed-in workspace, where the
  // Production Line follows its analysis; this public preview stops here.
  if (view.profile.reviewState === 'APPROVED' && !showProfile) {
    return <Navigate to="/production-lines" replace />;
  }

  return (
    <main className="mj-business-profile-page" lang={language}>
      <section className="mj-business-profile-wrap">
        {origin === 'production-lines' ? (
          <nav className="mj-business-profile-breadcrumb" aria-label={copy.breadcrumb}>
            <Link to="/production-lines">{copy.productionLines}</Link>
            <span aria-hidden="true">›</span>
            <span>{view.business.displayName}</span>
          </nav>
        ) : null}
        <header className="mj-business-profile-intro">
          <div className="mj-business-profile-title-row">
            <h1>{copy.title}</h1>
            {view.profile.reviewState === 'APPROVED' ? (
              <span className="mj-business-profile-confirmed">
                <Check size={15} aria-hidden="true" /> {copy.confirmed}
              </span>
            ) : null}
          </div>
          <p>{copy.intro}</p>
        </header>

        {error !== null ? (
          <div className="mj-business-profile-error">
            <InlineAlert tone="error">{error}</InlineAlert>
          </div>
        ) : null}

        <div className="mj-business-profile-layout">
          <article className="mj-business-profile-document">
            {editing ? (
              <section aria-labelledby="correct-profile-title">
                <h2 id="correct-profile-title">{copy.correctTitle}</h2>
                <p className="mj-business-profile-helper">{copy.correctHelp}</p>
                <div className="mj-business-profile-fields">
                  <EditField
                    label={copy.businessName}
                    value={identity}
                    onChange={setIdentity}
                    invalid={identity.trim().length === 0}
                  />
                  <EditField
                    label={copy.businessDoes}
                    value={niche}
                    onChange={setNiche}
                    invalid={niche.trim().length === 0}
                  />
                  <GeographyCombobox
                    label={copy.geography}
                    value={geography}
                    invalid={geography.trim().length === 0}
                    onChange={setGeography}
                  />
                  <fieldset
                    className={`mj-business-profile-offer-editor${
                      offerTitles.some((title) => title.trim().length > 0)
                        ? ''
                        : ' is-missing'
                    }`}
                  >
                    <legend>
                      {copy.offers} <span>{copy.offerRequired}</span>
                    </legend>
                    {offerTitles.map((title, index) => (
                      <div key={String(index)}>
                        <label>
                          {copy.offer} {index + 1}
                          <input
                            value={title}
                            onChange={(event) => {
                              const next = [...offerTitles];
                              next[index] = event.target.value;
                              setOfferTitles(next);
                            }}
                          />
                        </label>
                        <button
                          type="button"
                          aria-label={`${copy.removeOffer} ${index + 1}`}
                          onClick={() => {
                            setOfferTitles((all) =>
                              all.filter((_, itemIndex) => itemIndex !== index),
                            );
                          }}
                        >
                          <Trash2 size={16} aria-hidden="true" />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      className="mj-business-profile-add-offer"
                      onClick={() => {
                        setOfferTitles((all) => [...all, '']);
                      }}
                    >
                      <Plus size={16} aria-hidden="true" /> {copy.addOffer}
                    </button>
                  </fieldset>
                </div>
                <div className="mj-business-profile-form-actions">
                  <button
                    type="button"
                    className="mj-profile-button mj-profile-button-secondary"
                    onClick={() => {
                      setEditing(false);
                    }}
                  >
                    {copy.cancel}
                  </button>
                  <button
                    type="button"
                    className="mj-profile-button mj-profile-button-primary"
                    disabled={working}
                    onClick={() => void save()}
                  >
                    {working ? copy.saving : copy.save}
                  </button>
                </div>
              </section>
            ) : (
              <>
                <section
                  className="mj-business-profile-name"
                  aria-labelledby="business-name"
                >
                  <p>{copy.businessName}</p>
                  <h2 id="business-name">{value(profile.identity)}</h2>
                </section>

                <dl className="mj-business-profile-facts">
                  <div>
                    <dt>{copy.businessDoes}</dt>
                    <dd>{value(profile.niche)}</dd>
                  </div>
                  <div>
                    <dt>{copy.geography}</dt>
                    <dd>{value(profile.geography)}</dd>
                  </div>
                </dl>

                <section className="mj-business-profile-section" id="profile-offers">
                  <h3>{copy.offers}</h3>
                  {profile.offers.length > 0 ? (
                    <ol className="mj-business-profile-offers">
                      {profile.offers.map((offer, index) => {
                        const meta = [offer.price.value, offer.deliveryMode.value]
                          .filter((item): item is string => Boolean(item?.trim()))
                          .join(' · ');
                        return (
                          <li key={offer.localId}>
                            <span>{String(index + 1).padStart(2, '0')}</span>
                            <div>
                              <strong>{value(offer.title)}</strong>
                              {meta ? <small>{meta}</small> : null}
                            </div>
                          </li>
                        );
                      })}
                    </ol>
                  ) : (
                    <div className="mj-business-profile-missing">
                      <AlertCircle size={15} aria-hidden="true" />
                      <div>
                        <strong>{copy.noOffers}</strong>
                        <span>{copy.noOffersHelp}</span>
                      </div>
                    </div>
                  )}
                </section>

                <section className="mj-business-profile-section">
                  <h3>{copy.sources}</h3>
                  <div className="mj-business-profile-sources">
                    {view.business.sources.map((source) => (
                      <a
                        key={source.canonicalUrl}
                        href={source.canonicalUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <span>{source.platform}</span>
                        <b dir="ltr">{merchantSourceIdentity(source)}</b>
                        <em>{copy.sourceReadOnly}</em>
                        <ExternalLink size={15} aria-hidden="true" />
                        <span className="mj-vh">{copy.opensNewTab}</span>
                      </a>
                    ))}
                  </div>
                  <p className="mj-business-profile-source-note">{copy.sourceNote}</p>
                </section>
              </>
            )}
          </article>

          <aside className="mj-business-profile-rail" aria-label={copy.actions}>
            {view.profile.reviewState !== 'APPROVED' ? (
              <>
                {missingConfirmationFields.length > 0 ? (
                  <div className="mj-business-profile-needed" role="status">
                    <h2>{copy.completeTitle}</h2>
                    <p>{copy.stillNeeded}</p>
                    <ul>
                      {missingConfirmationFields.map((field) => (
                        <li key={field}>{localizedMissingField(field, language)}</li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <div>
                    <h2>{copy.readyTitle}</h2>
                    <p>{copy.readyBody}</p>
                  </div>
                )}
                <button
                  type="button"
                  className="mj-profile-button mj-profile-button-primary"
                  disabled={working || missingConfirmationFields.length > 0}
                  onClick={() => void approve()}
                >
                  <Check size={17} aria-hidden="true" />{' '}
                  {working ? copy.confirming : copy.confirm}
                </button>
                <button
                  type="button"
                  className="mj-profile-button mj-profile-button-secondary"
                  onClick={
                    merchant
                      ? beginEdit
                      : () => {
                          openAuth('correct');
                        }
                  }
                >
                  <Pencil size={16} aria-hidden="true" /> {copy.edit}
                </button>
              </>
            ) : (
              <>
                <div className="mj-business-profile-success">
                  <Check size={18} aria-hidden="true" />
                  <div>
                    <h2>{copy.confirmedTitle}</h2>
                    <p>{copy.confirmedBody}</p>
                  </div>
                </div>
                <button
                  type="button"
                  className="mj-profile-button mj-profile-button-secondary"
                  onClick={beginEdit}
                >
                  <Pencil size={16} aria-hidden="true" /> {copy.editProfile}
                </button>
              </>
            )}
          </aside>
        </div>
      </section>

      {authIntent !== null && !merchant ? (
        <MerchantAuthModal
          intent={authIntent}
          close={() => {
            setAuthIntent(null);
          }}
        />
      ) : null}
    </main>
  );
}

function EditField(props: {
  readonly label: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly invalid?: boolean;
}) {
  return (
    <label
      className={`mj-business-profile-edit-field${props.invalid ? ' is-missing' : ''}`}
    >
      {props.label}
      <input
        aria-invalid={props.invalid || undefined}
        value={props.value}
        onChange={(event) => {
          props.onChange(event.target.value);
        }}
      />
    </label>
  );
}

function ProfileLoadError({
  detail,
  language,
  retry,
}: {
  readonly detail: string;
  readonly language: 'en' | 'ar';
  readonly retry: () => void;
}) {
  const copy = PROFILE_COPY[language];
  return (
    <Centered>
      <section className="mj-business-profile-load-error" role="alert">
        <AlertCircle size={20} aria-hidden="true" />
        <div>
          <h1>{copy.restoreTitle}</h1>
          <p>{detail}</p>
          <button
            type="button"
            className="mj-profile-button mj-profile-button-primary"
            onClick={retry}
          >
            {copy.retry}
          </button>
        </div>
      </section>
    </Centered>
  );
}

function MissingProfile({
  detail,
  language,
}: {
  readonly detail?: string;
  readonly language: 'en' | 'ar';
}) {
  const copy = PROFILE_COPY[language];
  return (
    <Centered>
      <InlineAlert tone="neutral" title={copy.missingTitle}>
        {detail ?? copy.missingBody}
      </InlineAlert>
    </Centered>
  );
}

function Centered({ children }: { readonly children: ReactNode }) {
  return (
    <main
      style={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        padding: 24,
        background: 'var(--surface-page)',
      }}
    >
      {children}
    </main>
  );
}
